import { toast } from './toast.js';
import { api, ApiError } from './api.js';
import { Outbox, type OutboxEntry } from './outbox.js';
import { connectOutbox, hasUnsafe } from './saveStatus.js';
import { keys, queryClient } from './queries.js';
import { realtime } from './realtime.js';
import { storage, tabId } from './storage.js';

const KEY = 'werft.outbox.v1';
const TABS = 'werft.tabs.v1';

async function send(e: OutboxEntry): Promise<unknown> {
  try {
    if (e.method === 'POST') return await api.post(e.url, e.body);
    if (e.method === 'PATCH') return await api.patch(e.url, e.body);
    if (e.method === 'PUT') return await api.put(e.url, e.body);
    return await api.del(e.url, e.body);
  } catch (err) {
    // Sitzung abgelaufen: Anmeldestatus prüfen; der Eintrag wartet, bis wieder angemeldet ist
    if (err instanceof ApiError && err.status === 401) void queryClient.invalidateQueries({ queryKey: keys.me });
    throw err;
  }
}

/** Der Ausgangskorb dieses Tabs (siehe `outbox.ts`) */
export const outbox = new Outbox({
  send,
  load: () => (storage.ok ? (storage.get<OutboxEntry[]>(KEY) ?? []) : undefined),
  save: (l) => storage.ok && storage.set(KEY, l),
  loadTabs: () => storage.get<Record<string, number>>(TABS),
  saveTabs: (t) => void storage.set(TABS, t),
  now: () => Date.now(),
  tab: tabId(),
  setTimer: (fn, ms) => setTimeout(fn, ms),
  clearTimer: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
});
// ADR 0175: objektbezogener Speicherstatus liest offene und abgeschlossene Einträge mit
connectOutbox(outbox);

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) outbox.reload();
  });
  window.addEventListener('online', () => outbox.wake());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') outbox.wake();
  });
  outbox.heartbeat();
  setInterval(() => outbox.heartbeat(), 5_000);
  // Nur wenn nicht dauerhaft gespeichert werden kann: vor dem Schließen mit offenen Änderungen warnen
  window.addEventListener('beforeunload', (e) => {
    // R04: auch Entwürfe, die nur im Arbeitsspeicher liegen (Beschreibung bei vollem Speicher) – Warnung als Rückfallebene
    if ((outbox.volatile && outbox.getStatus().pending > 0) || hasUnsafe()) e.preventDefault();
  });
}

// Live-Verbindung wieder da: offene Änderungen sofort senden
realtime.subscribeStatus(() => {
  if (realtime.getStatus() === 'online') outbox.wake();
});

// Auch nach Neuladen bekannt: Verwaltungsänderungen dürfen nicht verworfen werden.
outbox.register('settings.save', {
  done() {
    for (const queryKey of [['admin'], ['appearance'], keys.me, keys.config]) void queryClient.invalidateQueries({ queryKey });
  },
  fail(_entry, err) { toast((err as Error).message, { kind: 'error' }); },
});
